/**
 * Utility functions for timing-related operations such as debouncing and throttling.
 */

/**
 * Creates a debounced function that delays the invocation of the provided function
 * until after the specified wait time has elapsed since the last time it was invoked.
 *
 * @param func - The function to debounce.
 * @param wait - The number of milliseconds to delay.
 * @returns A new debounced function.
 *
 * @example
 * const debouncedLog = debounce(() => console.log('Debounced!'), 300);
 * window.addEventListener('resize', debouncedLog);
 */
export function debounce<T extends (...args: any[]) => void>(func: T, wait: number): T {
  let timeout: number | undefined
  return function (this: any, ...args: any[]) {
    if (timeout !== undefined) {
      clearTimeout(timeout)
    }
    timeout = window.setTimeout(() => func.apply(this, args), wait)
  } as T
}

/**
 * Creates a throttled function that only invokes the provided function
 * at most once per every specified limit of milliseconds.
 *
 * @param func - The function to throttle.
 * @param limit - The number of milliseconds to throttle invocations to.
 * @returns A new throttled function.
 *
 * @example
 * const throttledLog = throttle(() => console.log('Throttled!'), 300);
 * window.addEventListener('scroll', throttledLog);
 */
export function throttle<T extends (...args: any[]) => void>(func: T, limit: number): T {
  let inThrottle: boolean = false
  return function (this: any, ...args: any[]) {
    console.log('Throttling function', func.name, 'with limit', limit)
    if (!inThrottle) {
      console.log('Invoking throttled function', func.name)
      func.apply(this, args)
      inThrottle = true
      window.setTimeout(() => (inThrottle = false), limit)
    }
  } as T
}
